// Smart Daara - Physical school: subjects + coefficients per level
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SubjectsPage() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [levelSubjects, setLevelSubjects] = useState([]);
  const [form, setForm] = useState({ name: '', color: '#10b981' });

  const load = async () => {
    const [s, l] = await Promise.all([physicalAPI.listSubjects(), physicalAPI.listLevels()]);
    setSubjects(s.data); setLevels(l.data);
    if (!selectedLevel && l.data.length) setSelectedLevel(l.data[0].id);
  };
  useEffect(() => { load().catch(() => {}); }, []);
  useEffect(() => {
    if (selectedLevel) physicalAPI.listLevelSubjects(selectedLevel).then((r) => setLevelSubjects(r.data));
  }, [selectedLevel]);

  const addSubject = async () => {
    if (!form.name.trim()) return;
    await physicalAPI.createSubject(form);
    setForm({ name: '', color: '#10b981' });
    load();
    toast.success(fr ? 'Matière créée' : 'Subject created');
  };

  const getLS = (subjectId) => levelSubjects.find((ls) => ls.subject_id === subjectId);

  const saveCoef = async (subjectId, coefficient, weekly_hours) => {
    await physicalAPI.setLevelSubject({ level_id: selectedLevel, subject_id: subjectId, coefficient, weekly_hours });
    physicalAPI.listLevelSubjects(selectedLevel).then((r) => setLevelSubjects(r.data));
    toast.success(fr ? 'Enregistré' : 'Saved');
  };

  return (
    <div className="space-y-6" data-testid="subjects-page">
      <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Matières & coefficients' : 'Subjects & coefficients'}</h1>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Matières' : 'Subjects'}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]"><Label className="text-xs">{fr ? 'Nom' : 'Name'}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="subject-name-input" /></div>
            <div><Label className="text-xs">{fr ? 'Couleur' : 'Color'}</Label><Input type="color" className="w-14 p-1" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <Button className="bg-emerald-700" onClick={addSubject} data-testid="add-subject-btn">{fr ? 'Ajouter' : 'Add'}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {subjects.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm text-white" style={{ backgroundColor: s.color }}>
                {s.name}
                <button onClick={async () => {
                  try { await physicalAPI.deleteSubject(s.id); load(); }
                  catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                }} className="opacity-70 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">{fr ? 'Coefficients & volume horaire par niveau' : 'Coefficients & weekly hours per level'}</CardTitle>
            <select className="h-10 border border-slate-200 rounded-md px-2 text-sm" value={selectedLevel} onChange={(e) => setSelectedLevel(e.target.value)} data-testid="coef-level-select">
              {levels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {!selectedLevel ? <p className="text-slate-400 text-sm">{fr ? 'Créez d\'abord un niveau' : 'Create a level first'}</p> : (
            <div className="space-y-2">
              {subjects.map((s) => {
                const ls = getLS(s.id);
                return <CoefRow key={s.id + selectedLevel} subject={s} ls={ls} fr={fr} onSave={saveCoef} />;
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const CoefRow = ({ subject, ls, fr, onSave }) => {
  const [coef, setCoef] = useState(ls?.coefficient ?? '');
  const [hours, setHours] = useState(ls?.weekly_hours ?? '');
  return (
    <div className="grid grid-cols-4 gap-2 items-center px-3 py-2 bg-slate-50 rounded-lg">
      <span className="font-medium flex items-center gap-2">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: subject.color }} />{subject.name}
      </span>
      <Input type="number" step="0.5" placeholder={fr ? 'Coef' : 'Coef'} value={coef} onChange={(e) => setCoef(e.target.value)} data-testid={`coef-input-${subject.id}`} />
      <Input type="number" step="0.5" placeholder={fr ? 'h/semaine' : 'h/week'} value={hours} onChange={(e) => setHours(e.target.value)} />
      <Button size="sm" variant="outline" disabled={coef === '' || hours === ''} onClick={() => onSave(subject.id, parseFloat(coef), parseFloat(hours))} data-testid={`coef-save-${subject.id}`}>
        {ls ? (fr ? 'Mettre à jour' : 'Update') : (fr ? 'Activer pour ce niveau' : 'Enable for level')}
      </Button>
    </div>
  );
};
